import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './RoutineBuilder.module.css'

interface RoutineItem { id: string; name: string; emoji: string; duration?: number; isCustom?: boolean }
interface Section { id: string; label: string; emoji: string; items: RoutineItem[] }

const STORAGE_KEY = 'mellomap-routines'
const PROGRESS_KEY = 'mellomap-routine-progress'

const DEFAULT_SECTIONS: Section[] = [
  { id: 'morning', label: 'Morning', emoji: '', items: [
    { id: 'm1', name: 'Wake up', emoji: '', duration: 5 },
    { id: 'm2', name: 'Brush teeth', emoji: '', duration: 5 },
    { id: 'm3', name: 'Bath / wash face', emoji: '', duration: 10 },
    { id: 'm4', name: 'Get dressed', emoji: '', duration: 10 },
    { id: 'm5', name: 'Breakfast', emoji: '', duration: 15 },
    { id: 'm6', name: 'Pack school bag', emoji: '', duration: 5 },
  ]},
  { id: 'afternoon', label: 'Afternoon', emoji: '', items: [
    { id: 'a1', name: 'Lunch', emoji: '', duration: 15 },
    { id: 'a2', name: 'Quiet time / nap', emoji: '', duration: 30 },
    { id: 'a3', name: 'Snack', emoji: '', duration: 10 },
    { id: 'a4', name: 'Homework / coloring', emoji: '', duration: 20 },
    { id: 'a5', name: 'Outdoor play', emoji: '', duration: 30 },
  ]},
  { id: 'evening', label: 'Evening', emoji: '', items: [
    { id: 'e1', name: 'Indoor play', emoji: '', duration: 20 },
    { id: 'e2', name: 'Help with dinner', emoji: '', duration: 10 },
    { id: 'e3', name: 'Dinner', emoji: '', duration: 15 },
    { id: 'e4', name: 'Clean up toys', emoji: '', duration: 5 },
  ]},
  { id: 'bedtime', label: 'Bedtime', emoji: '', items: [
    { id: 'b1', name: 'Change into night clothes', emoji: '', duration: 5 },
    { id: 'b2', name: 'Brush teeth', emoji: '', duration: 5 },
    { id: 'b3', name: 'Story time', emoji: '', duration: 10 },
    { id: 'b4', name: 'Oil massage (malish)', emoji: '', duration: 5 },
    { id: 'b5', name: 'Goodnight kisses', emoji: '', duration: 2 },
    { id: 'b6', name: 'Lights out', emoji: '', duration: 0 },
  ]},
]

function loadSections(): Section[] { const d = localStorage.getItem(STORAGE_KEY); return d ? JSON.parse(d) : DEFAULT_SECTIONS }
function saveSections(s: Section[]) { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)) }
function todayKey() { return `${PROGRESS_KEY}-${new Date().toISOString().split('T')[0]}` }
function loadProgress(): string[] { const d = localStorage.getItem(todayKey()); return d ? JSON.parse(d) : [] }
function saveProgress(p: string[]) { localStorage.setItem(todayKey(), JSON.stringify(p)) }

export default function RoutineBuilder() {
  const navigate = useNavigate()
  const [sections, setSections] = useState<Section[]>(loadSections)
  const [done, setDone] = useState<string[]>(loadProgress)
  const [activeTab, setActiveTab] = useState('morning')
  const [newItemName, setNewItemName] = useState('')

  const activeSection = sections.find(s => s.id === activeTab)!
  const doneCount = activeSection.items.filter(i => done.includes(i.id)).length

  const toggleDone = (id: string) => {
    const updated = done.includes(id) ? done.filter(d => d !== id) : [...done, id]
    setDone(updated); saveProgress(updated)
  }

  const moveItem = (index: number, direction: -1 | 1) => {
    const newIdx = index + direction
    if (newIdx < 0 || newIdx >= activeSection.items.length) return
    const items = [...activeSection.items]
    ;[items[index], items[newIdx]] = [items[newIdx], items[index]]
    const updated = sections.map(s => s.id === activeTab ? { ...s, items } : s)
    setSections(updated); saveSections(updated)
  }

  const removeItem = (id: string) => {
    const items = activeSection.items.filter(i => i.id !== id)
    const updated = sections.map(s => s.id === activeTab ? { ...s, items } : s)
    setSections(updated); saveSections(updated)
  }

  const addItem = () => {
    if (!newItemName.trim()) return
    const item: RoutineItem = { id: `custom-${Date.now()}`, name: newItemName.trim(), emoji: '', isCustom: true }
    const items = [...activeSection.items, item]
    const updated = sections.map(s => s.id === activeTab ? { ...s, items } : s)
    setSections(updated); saveSections(updated); setNewItemName('')
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>←</button>
        <h1 className={styles.title}>Daily Routines</h1>
      </header>

      <div className={styles.tip}>Same steps, same order, every day. Your child's brain thrives on predictability.</div>

      <div className={styles.tabs}>
        {sections.map(s => (
          <button key={s.id} className={`${styles.tab} ${activeTab === s.id ? styles.tabActive : ''}`} onClick={() => setActiveTab(s.id)}>
            <span>{s.emoji}</span><span>{s.label}</span>
          </button>
        ))}
      </div>

      <div className={styles.progressLine}>
        <span>{doneCount} of {activeSection.items.length} done</span>
        <div className={styles.progressBar}><div className={styles.progressFill} style={{ width: `${activeSection.items.length > 0 ? (doneCount / activeSection.items.length) * 100 : 0}%` }} /></div>
      </div>

      <div className={styles.items}>
        {activeSection.items.map((item, i) => (
          <div key={item.id} className={`${styles.item} ${done.includes(item.id) ? styles.itemDone : ''}`}>
            <input type="checkbox" checked={done.includes(item.id)} onChange={() => toggleDone(item.id)} className={styles.checkbox} />
            <span className={styles.itemEmoji}>{item.emoji}</span>
            <span className={styles.itemName}>{item.name}</span>
            {item.duration ? <span className={styles.itemDuration}>{item.duration}m</span> : null}
            <div className={styles.itemActions}>
              <button className={styles.moveBtn} onClick={() => moveItem(i, -1)} disabled={i === 0}>↑</button>
              <button className={styles.moveBtn} onClick={() => moveItem(i, 1)} disabled={i === activeSection.items.length - 1}>↓</button>
              <button className={styles.removeBtn} onClick={() => removeItem(item.id)}>✕</button>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.addRow}>
        <input className={styles.addInput} value={newItemName} onChange={e => setNewItemName(e.target.value)} placeholder="Add custom step..." onKeyDown={e => e.key === 'Enter' && addItem()} />
        <button className={styles.addBtn} onClick={addItem} disabled={!newItemName.trim()}>Add</button>
      </div>
    </div>
  )
}
